/**
 * VideoUploadInput
 * ────────────────
 * Sibling of ImageUploadInput, but for short promo videos. Non-technical
 * operators can paste a video URL OR pick a file — the file is uploaded to
 * the public `sponsor-creatives` bucket and the resulting public URL is
 * written back via `onChange`.
 *
 * Unlike ad-campaign creative video (which overlays the ride flow and is
 * capped hard at 10s/25MB), festival promo videos are OPT-IN viewing on the
 * festival detail sheet, so we allow a larger, longer clip — a carnival
 * teaser reel doesn't fit in 10 seconds. Callers tune the caps via props.
 */

import { useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { deleteSponsorCreativeByUrl } from '@/lib/storageCleanup';

const BUCKET = 'sponsor-creatives';
const ACCEPTED = 'video/mp4,video/quicktime';
const ACCEPTED_SET = new Set(['video/mp4', 'video/quicktime']);

export interface VideoUploadInputProps {
  value: string;
  onChange: (url: string) => void;
  /** Storage sub-folder, e.g. 'festival-promos'. */
  folder?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Max file size in MB. Default 60 (festival promos). */
  maxMb?: number;
  /** Max duration in seconds. Default 90. Set 0 to skip the check. */
  maxSeconds?: number;
}

/** Read a video file's duration (seconds) without uploading it. */
function readVideoDuration(file: File): Promise<number> {
  return new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const el = document.createElement('video');
      el.preload = 'metadata';
      el.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        resolve(Number.isFinite(el.duration) ? el.duration : 0);
      };
      el.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(0);
      };
      el.src = url;
    } catch {
      resolve(0);
    }
  });
}

export function VideoUploadInput({
  value,
  onChange,
  folder = 'festival-promos',
  placeholder = 'https://…  or upload a video →',
  disabled = false,
  maxMb = 60,
  maxSeconds = 90,
}: VideoUploadInputProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  // URL of the file THIS component uploaded during the current session.
  // Only session uploads are safe to auto-delete on replace/clear — a
  // pre-existing (persisted) value is cleaned up by the parent on save.
  const sessionUploadRef = useRef<string | null>(null);

  const maxBytes = maxMb * 1024 * 1024;

  const handleFile = async (file: File) => {
    if (!file) return;
    if (!ACCEPTED_SET.has(file.type)) {
      toast.error('Video must be an MP4 or MOV file. MP4 plays on the most phones.');
      return;
    }
    if (file.size > maxBytes) {
      toast.error(
        `Video is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max ${maxMb} MB.`,
      );
      return;
    }
    if (maxSeconds > 0) {
      const dur = await readVideoDuration(file);
      if (dur > 0 && dur > maxSeconds + 0.5) {
        toast.error(
          `This clip is ${Math.round(dur)}s. Please trim it to ${maxSeconds} seconds or less.`,
        );
        return;
      }
    }
    setUploading(true);
    try {
      // Read the bytes first so an unreadable iCloud/Photos placeholder
      // (which can report a nonzero size yet yield no data) fails here
      // instead of shipping an empty multipart body that Storage rejects
      // as "No content provided". Uploading the raw buffer also means our
      // contentType is honoured (storage-js ignores it on the File path).
      let bytes: ArrayBuffer;
      try {
        bytes = await file.arrayBuffer();
      } catch {
        bytes = new ArrayBuffer(0);
      }
      if (!bytes.byteLength) {
        toast.error(
          'That video came through empty or unreadable. If it lives in Photos or iCloud, export/download it to your Downloads folder first, then upload that copy.',
        );
        return;
      }
      const ext = file.name.split('.').pop()?.toLowerCase() || 'mp4';
      const path = `${folder}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const { error: uploadErr } = await supabase.storage
        .from(BUCKET)
        .upload(path, bytes, {
          contentType: file.type || 'video/mp4',
          upsert: false,
          cacheControl: '31536000',
        });
      if (uploadErr) throw uploadErr;
      const { data } = supabase.storage.from(BUCKET).getPublicUrl(path);
      // Reclaim the previous session upload we're superseding.
      if (sessionUploadRef.current && sessionUploadRef.current !== data.publicUrl) {
        void deleteSponsorCreativeByUrl(sessionUploadRef.current);
      }
      sessionUploadRef.current = data.publicUrl;
      onChange(data.publicUrl);
      toast.success('Video uploaded');
    } catch (err: any) {
      const raw = String(err?.message || '');
      if (/no content provided/i.test(raw)) {
        toast.error(
          'Upload failed: the file arrived empty. If it came from Photos or iCloud, export it to your device first, then upload that copy.',
        );
      } else if (/exceeded the maximum allowed size|payload too large/i.test(raw)) {
        toast.error(`That video is over the ${maxMb} MB limit.`);
      } else {
        toast.error(raw || 'Upload failed');
      }
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="bg-gray-800 border-gray-700 text-white flex-1"
        />
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED}
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleFile(f);
          }}
        />
        {value && !uploading && (
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            onClick={() => {
              // Only delete from storage if we uploaded it this session;
              // a persisted value is handled by the parent on save.
              if (sessionUploadRef.current && sessionUploadRef.current === value) {
                void deleteSponsorCreativeByUrl(value);
                sessionUploadRef.current = null;
              }
              onChange('');
            }}
            className="border-gray-700 text-gray-400 hover:bg-gray-800 hover:text-white shrink-0"
            title="Remove video"
          >
            <X className="w-4 h-4" />
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          disabled={disabled || uploading}
          onClick={() => inputRef.current?.click()}
          className="border-purple-500/40 text-purple-300 hover:bg-purple-500/10 hover:text-purple-200 shrink-0"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          <span className="ml-1.5 hidden sm:inline">Upload</span>
        </Button>
      </div>
      {value && (
        <video
          src={value}
          className="w-full max-h-48 rounded-lg border border-gray-800 bg-black"
          controls
          playsInline
          muted
        />
      )}
    </div>
  );
}
